import type { Client } from 'openapi-fetch';

import { createApiClient } from '../http/createApiClient';
import { createAuthorizedFetch, type AuthorizedFetch } from '../http/createAuthorizedFetch';
import type { AuthSession } from '../ports';
import type { AppleEedPaths } from './apple-eed';
import type { EmsPaths } from './ems';
import type { IdentityPaths } from './identity';

export type ApiClientsConfiguration = {
  emsBaseUrl: string;
  appleEedBaseUrl?: string | undefined;
  identityBaseUrl?: string | undefined;
  authSession: AuthSession;
};

let configuration: ApiClientsConfiguration | undefined;
let ems: Client<EmsPaths> | undefined;
let appleEed: Client<AppleEedPaths> | undefined;
let identity: Client<IdentityPaths> | undefined;
let authFetch: AuthorizedFetch | undefined;

export function configureApiClients(config: ApiClientsConfiguration): void {
  configuration = { ...config };
  ems = undefined;
  appleEed = undefined;
  identity = undefined;
  authFetch = undefined;
}

function ensureConfigured(): ApiClientsConfiguration {
  if (configuration === undefined) {
    throw new Error(
      'API clients are not configured. Call configureApiClients() during bootstrap first.',
    );
  }
  return configuration;
}

export function apiAuthSession(): AuthSession {
  return ensureConfigured().authSession;
}

export function emsBaseUrl(): string {
  return ensureConfigured().emsBaseUrl;
}

export function appleEedBaseUrl(): string {
  const url = ensureConfigured().appleEedBaseUrl;
  if (url === undefined) {
    throw new Error(
      'The Apple EED base URL is not configured. Pass appleEedBaseUrl to configureApiClients() during bootstrap first.',
    );
  }
  return url;
}

export function identityBaseUrl(): string {
  const url = ensureConfigured().identityBaseUrl;
  if (url === undefined) {
    throw new Error(
      'The Identity base URL is not configured. Pass identityBaseUrl to configureApiClients() during bootstrap first.',
    );
  }
  return url;
}

export function emsClient(): Client<EmsPaths> {
  ems ??= createApiClient<EmsPaths>(emsBaseUrl(), apiAuthSession());
  return ems;
}

export function appleEedClient(): Client<AppleEedPaths> {
  appleEed ??= createApiClient<AppleEedPaths>(appleEedBaseUrl(), apiAuthSession());
  return appleEed;
}

export function identityClient(): Client<IdentityPaths> {
  identity ??= createApiClient<IdentityPaths>(identityBaseUrl(), apiAuthSession());
  return identity;
}

export function authorizedFetch(): AuthorizedFetch {
  authFetch ??= createAuthorizedFetch(apiAuthSession());
  return authFetch;
}
